import { observable, action, computed } from 'mobx';
import Alert from 'react-s-alert';

import { AppRegistry, ModelRegistry, StoreRegistry } from 'core/registry';
import { App, ArrayApp } from 'core';
import { analytics } from 'core/analytics';

import { PermissionedModel } from 'apps/permissions/app';
import { UserModel, UserApp } from 'apps/user/app';
import { FileModel, FileApp } from 'apps/file/app';
import { TagsApp } from 'apps/tag/app';
import { SpaceModel, SpaceApp } from 'apps/space/app';
import { SocialAccountModel, SocialAccountsApp } from 'apps/socialAccounts/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { ConnResultsModal } from 'apps/modals/connResultsModal/index';
import { ThreeColumnLayout } from 'apps/layout';
import { conf as tagsConf } from 'apps/tag/config.yaml';
import { conf as globalConf } from 'apps/config.yaml';

import { conf } from 'apps/profile/config.yaml';

import i18next from 'i18next';

const { imageDefaults } = globalConf;

export class ProfileModel extends PermissionedModel {
  static registryName = 'ProfileModel';
  static ModelProps = {
    user: UserModel,
    avatar: FileModel,
    skills: ['TagModel'],
    socialAccounts: [SocialAccountModel],
    inSpace: SpaceModel,
    content: 'ContentModel',
    billingSubscriptions: ['SubscriptionModel'],
    invoices: ['InvoiceModel'],
    creditCards: ['CreditCardModel'],
    seatsUsage: 'SeatsUsageModel',
    subscriptionPreview: 'SubscriptionPreviewModel',
  };

  @observable username = '@';
  @observable profileConfirmed = true;
  @observable fullName = '';
  @observable locale = '';
  @observable preferredName = '';
  @observable jobTitleCompany = '';
  @observable avatar = null;
  @observable bio = '';
  @observable followingCount = 0;
  @observable user = null;
  @observable intent = '';
  @observable userType = '';
  @observable skills = [];
  @observable socialAccounts = [];
  @observable imageFallbackChar = '';
  @observable content = null;
  @observable contributedProjects = null;
  @observable invitesLeft = 0;
  @observable billingSubscriptions = [];
  @observable hasValidPaymentMethod = false;
  @observable invoices = [];
  @observable creditCards = [];
  @observable seatsUsage = null;
  @observable subscriptionPreview = null;
  @observable countryCode = '';
  @observable taxId = '';
  @observable contributions = null;
  @observable startFreeTrial = false;
  @observable management = '';
  @observable promotion = null;

  @computed get imageFallbackPath() {
    if (this.imageFallbackChar) {
      return `/assets/avatars/${this.imageFallbackChar}.jpeg`;
    }
    return imageDefaults.profile;
  }

  @observable inSpace = null;
}
ModelRegistry.add(ProfileModel);

export class ProfileApp extends App {
  static registryName = 'ProfileApp';
  static AppProps = {
    skills: TagsApp,
    socialAccounts: SocialAccountsApp,
    user: UserApp,
    avatar: FileApp,
    inSpace: SpaceApp,
    content: 'ContentApp',
    billingSubscriptions: 'SubscriptionsArrayApp',
    invoices: 'InvoiceArrayApp',
    creditCards: 'CreditCardArrayApp',
    seatsUsage: 'SeatsUsageApp',
    subscriptionPreview: 'SubscriptionPreviewApp',
  };

  static Model = ProfileModel;
  @observable intent = '';
  @observable userType = '';
  @observable editing = false;
  @observable saving = false;
  @observable gatewayForm = null;
  @observable email = '';
  createGatewayForm(options = { email: false }) {
    const { email } = options;
    const { gatewayProfile, ...restConf } = conf;
    const patchedGatewayProfile = {
      ...gatewayProfile,
      fields: ['email', ...gatewayProfile.fields],
    };
    const patchedConf = {
      ...restConf,
      gatewayProfile: email ? patchedGatewayProfile : gatewayProfile,
    };
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.gatewayForm = new FormStore(this, patchedConf, 'gatewayProfile');
  }

  @computed get route() {
    if (!this.username) return {};
    return { name: 'profile', params: { profile: this.username } };
  }

  @computed get isOwnProfile() {
    return (
      clientStore.signedInProfile &&
      clientStore.signedInProfile.username === `${this.user.username}`
    );
  }

  @computed get managementData() {
    try {
      const data = JSON.parse(this.management);
      return data || {};
    } catch {
      return {};
    }
  }

  @action SetSaving = saving => {
    this.saving = saving;
  };

  @action SetEditing = editing => {
    this.editing = editing;
  };

  @action SetDeleted = deleted => {
    this.deleted = deleted;
  };

  @action Edit = () => {
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.editFormStore = new FormStore(this, conf, 'editProfile');
    this.SetEditing(true);
    analytics.sendEvent('edited-profile', {}, { ga: true });
  };

  @action EditTags = () => {
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.editTagsFormStore = new FormStore(this, tagsConf, 'editProfileSkills');
  };

  @action DoneEditingTags = () => {
    modalStore.ClearComponent();
    this.editTagsFormStore = null;
  };

  @action ShowInitiatives = () => {
    modalStore.SetModalComponent(ConnResultsModal, {
      arrayApps: [this.manager.initiativesConn.arrayApp],
      modalTitle: `${this.fullName} is a member of:`,
    });
  };

  @action ShowFollowers = () => {
    if (['s', 'xs'].includes(clientStore.activeBreakPoint)) {
      return;
    }
    let title = `${this.fullName}'s followers:`;
    if (
      clientStore.signedInProfile &&
      clientStore.signedInProfile.username === this.username
    ) {
      title = 'Your followers:';
    }
    modalStore.SetModalComponent(ConnResultsModal, {
      arrayApps: [this.manager.followersConn.arrayApp],
      modalTitle: title,
    });
  };

  @action ShowFollowing = () => {
    if (['s', 'xs'].includes(clientStore.activeBreakPoint)) {
      return;
    }
    let title = `${this.fullName} is following:`;
    if (
      clientStore.signedInProfile &&
      clientStore.signedInProfile.username === this.username
    ) {
      title = 'You are following:';
    }
    modalStore.SetModalComponent(
      ConnResultsModal,
      {
        arrayApps: [
          this.manager.followingProfilesConn.arrayApp,
          this.manager.followingInitiativesConn.arrayApp,
          this.manager.followingProjectsConn.arrayApp,
        ],
        modalTitle: title,
      },
      () => {
        this.manager.FollowingUpdate();
      },
    );
  };
}
AppRegistry.add(ProfileApp);

export class ProfilesApp extends ArrayApp {
  static registryName = 'ProfilesApp';
  static ElementApp = ProfileApp;
  cardLayout = new ThreeColumnLayout();

  // eslint-disable-next-line
  getDup(profile) {
    // eslint-disable-next-line
    for (const p of this) {
      if (p.username === profile.username) {
        return p;
      }
    }
  }

  @action Add = profile => {
    const dup = this.getDup(profile);
    if (dup) {
      if (dup.deleted) {
        dup.deleted = false;
      } else {
        const { username } = profile;
        Alert.error(
          i18next.t("No need to add '{{username}}' twice!", { username }),
        );
      }
    } else {
      this.push(profile);
    }
  };

  @action Delete = profile => {
    const exists = this.upstream.findIndex(p => p.id === profile.id);
    if (exists === -1) {
      this.remove(profile);
    } else {
      profile.SetDeleted(true);
    }
  };
}
AppRegistry.add(ProfilesApp);
